import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listKitSkus, listKitComponents,
  listWorkOrders, listFacilityLocations, createWorkOrder,
} from "@/lib/inventory-catalog.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { Loader2, Plus, Eye, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/inventory/kits/")({ component: KitsPage });

function KitsPage() {
  const { tenantId, customerId } = useScope();
  const listKitSkusFn = useServerFn(listKitSkus);
  const [viewKitId, setViewKitId] = useState<string | null>(null);

  const { data: kits = [], isLoading } = useQuery({
    queryKey: ["kit-skus", tenantId, customerId],
    enabled: !!tenantId,
    queryFn: () => listKitSkusFn({ data: { tenantId: tenantId!, customerId: customerId ?? null } }),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Kits & Co-Packing</h2>
        <p className="text-sm text-muted-foreground">Kits are finished goods produced via a work order — orders can only ship when kit inventory is on hand. Toggle "Kit (finished good)" on a SKU and edit its components from the SKUs page. Bundles (sold-as-group) live on the Bundles tab.</p>
      </div>

      <div className="rounded-md border">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-medium">Kit Catalog</h3>
          <Badge variant="outline">{kits.length} kits</Badge>
        </div>
        {isLoading ? (
          <div className="p-8 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Name</TableHead><TableHead className="text-right">BOM</TableHead></TableRow></TableHeader>
            <TableBody>
              {kits.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">No kit SKUs. Toggle "Kit" on a SKU to add one.</TableCell></TableRow>}
              {kits.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-mono text-sm">{s.sku_code}</TableCell>
                  <TableCell>{s.name}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => setViewKitId(s.id)}><Eye className="h-4 w-4 mr-1" /> View BOM</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <WorkOrdersSection kits={kits} />

      {viewKitId && <BomViewDialog kitId={viewKitId} onClose={() => setViewKitId(null)} kit={kits.find((k) => k.id === viewKitId) ?? null} />}
    </div>
  );
}

function BomViewDialog({ kitId, onClose, kit }: { kitId: string; onClose: () => void; kit: { id: string; sku_code: string; name: string } | null }) {
  const listComponentsFn = useServerFn(listKitComponents);
  const { data: comps = [] } = useQuery({
    queryKey: ["kit-components", kitId],
    queryFn: () => listComponentsFn({ data: { kitId } }),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>BOM — {kit?.sku_code} {kit?.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Table>
            <TableHeader><TableRow><TableHead>Component</TableHead><TableHead>Qty / kit</TableHead></TableRow></TableHeader>
            <TableBody>
              {comps.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-4">No components defined. Edit this kit on the SKUs page.</TableCell></TableRow>}
              {comps.map((c: any) => (
                <TableRow key={c.id}>
                  <TableCell><span className="font-mono text-sm">{c.skus?.sku_code}</span> <span className="text-muted-foreground">{c.skus?.name}</span></TableCell>
                  <TableCell>{c.qty_per_kit}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="text-xs text-muted-foreground">To edit components, go to the SKUs page and open this kit.</p>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WorkOrdersSection({ kits }: { kits: { id: string; sku_code: string; name: string }[] }) {
  const qc = useQueryClient();
  const { tenantId, facilityId, customers } = useScope();
  const listWorkOrdersFn = useServerFn(listWorkOrders);
  const listLocationsFn = useServerFn(listFacilityLocations);
  const createWorkOrderFn = useServerFn(createWorkOrder);
  const [open, setOpen] = useState(false);
  const [parent, setParent] = useState(""); const [qty, setQty] = useState("1");
  const [dst, setDst] = useState(""); const [due, setDue] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [filterCustomer, setFilterCustomer] = useState<string>("__all");
  const [sortKey, setSortKey] = useState<"created_desc" | "created_asc" | "due_asc" | "due_desc">("created_desc");

  const { data: orders = [] } = useQuery({
    queryKey: ["work-orders", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listWorkOrdersFn({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });

  const { data: locations = [] } = useQuery({
    queryKey: ["wo-locations", facilityId],
    enabled: !!facilityId,
    queryFn: () => listLocationsFn({ data: { facilityId: facilityId!, activeOnly: true } }),
  });

  const create = async () => {
    if (!tenantId || !facilityId || !parent || !dst) return toast.error("Pick a kit and an assembly bin");
    const n = parseInt(qty, 10); if (!n || n <= 0) return toast.error("Qty must be > 0");
    setSubmitting(true);
    try {
      await runServerFn(
        () => createWorkOrderFn({ data: {
          tenantId, facilityId,
          parentSkuId: parent, qty: n,
          destLocationId: dst,
          expectedCompletionDate: due || null,
          notes: notes || null,
        } }),
        "Create work order",
      );
      toast.success("Work order created"); setOpen(false);
      setParent(""); setQty("1"); setDst(""); setDue(""); setNotes("");
      qc.invalidateQueries({ queryKey: ["work-orders"] });
    } catch { /* toast handled */ } finally { setSubmitting(false); }
  };

  const tenantCustomers = customers.filter((c) => c.tenant_id === tenantId);
  const visibleOrders = useMemo(() => {
    let rows = orders as any[];
    if (filterCustomer !== "__all") rows = rows.filter((o) => o.customer_id === filterCustomer);
    const cmp = (a: any, b: any) => {
      if (sortKey === "created_desc") return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      if (sortKey === "created_asc") return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      const av = a.expected_completion_date ?? "";
      const bv = b.expected_completion_date ?? "";
      // Push blanks to the end for both directions
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      return sortKey === "due_asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    };
    return [...rows].sort(cmp);
  }, [orders, filterCustomer, sortKey]);

  return (
    <div className="rounded-md border">
      <div className="px-4 py-3 border-b flex flex-wrap items-center gap-2 justify-between">
        <h3 className="font-medium">Work Orders</h3>
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <Select value={filterCustomer} onValueChange={setFilterCustomer}>
            <SelectTrigger className="h-8 w-[180px]"><SelectValue placeholder="Customer" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All customers</SelectItem>
              {tenantCustomers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sortKey} onValueChange={(v) => setSortKey(v as typeof sortKey)}>
            <SelectTrigger className="h-8 w-[200px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="created_desc">Newest created</SelectItem>
              <SelectItem value="created_asc">Oldest created</SelectItem>
              <SelectItem value="due_asc">Due soonest</SelectItem>
              <SelectItem value="due_desc">Due latest</SelectItem>
            </SelectContent>
          </Select>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-2" />New work order</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create work order</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Kit</Label>
                <Select value={parent} onValueChange={setParent}>
                  <SelectTrigger><SelectValue placeholder={kits.length === 0 ? "No kit SKUs available" : "Pick kit SKU"} /></SelectTrigger>
                  <SelectContent>{kits.map((k) => <SelectItem key={k.id} value={k.id}>{k.sku_code} — {k.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Qty to produce</Label><Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} /></div>
                <div><Label>Expected completion</Label><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} /></div>
              </div>
              <div><Label>Assemble at</Label>
                  <Select value={dst} onValueChange={setDst}>
                    <SelectTrigger><SelectValue placeholder="Assembly bin" /></SelectTrigger>
                    <SelectContent>{locations.map((l: any) => <SelectItem key={l.id} value={l.id}>{l.code}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">The bin where operators will physically assemble the kits. After assembly, a putaway task is created — the system suggests a storage destination and operators can override it.</p>
              </div>
              <div><Label>Notes</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
              <p className="text-xs text-muted-foreground">After creation, you'll allocate components from specific bins before picking begins.</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={create} disabled={submitting || kits.length === 0}>{submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Kit</TableHead><TableHead>Customer</TableHead><TableHead>Qty</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead><TableHead>Expected</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
        <TableBody>
          {visibleOrders.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No work orders match.</TableCell></TableRow>}
          {visibleOrders.map((o: any) => (
            <TableRow key={o.id}>
              <TableCell><span className="font-mono text-sm">{o.skus?.sku_code}</span> <span className="text-muted-foreground">{o.skus?.name}</span></TableCell>
              <TableCell className="text-sm">{o.customers?.name ?? <span className="text-muted-foreground">—</span>}</TableCell>
              <TableCell>{o.qty}</TableCell>
              <TableCell><StatusPill tone={statusTone(o.status)}>{o.status}</StatusPill></TableCell>
              <TableCell className="text-sm text-muted-foreground">{new Date(o.created_at).toLocaleString()}</TableCell>
              <TableCell className="text-sm text-muted-foreground">{o.expected_completion_date ?? "—"}</TableCell>
              <TableCell className="text-right">
                <Button asChild size="sm" variant="outline">
                  <Link to="/app/inventory/kits/$workOrderId" params={{ workOrderId: o.id }}>
                    Open <ArrowRight className="h-3 w-3 ml-1" />
                  </Link>
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}